import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw, LogOut, Radar, Newspaper, LayoutGrid,
  LineChart as LineChartIcon, TableProperties, FileDown, Search,
} from "lucide-react";
import {
  fetchSummary, fetchStatements, fetchComparison,
  triggerCollect, collectOneCountry, downloadWeeklyReport, clearToken,
} from "../lib/api";
import StatementFeed from "../components/StatementFeed";
import CountryCards from "../components/CountryCards";
import Timeline from "../components/Timeline";
import ComparisonTable from "../components/ComparisonTable";
import { RESEARCH_START, RESEARCH_END } from "../lib/constants";

export default function Dashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [summaries, setSummaries] = useState([]);
  const [statements, setStatements] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filterCountry, setFilterCountry] = useState("");
  const [dateFrom, setDateFrom] = useState(RESEARCH_START);
  const [dateTo, setDateTo] = useState(RESEARCH_END);

  const countriesByCode = useMemo(() => {
    const m = {};
    summaries.forEach((s) => {
      m[s.code] = { code: s.code, name_tr: s.name_tr, flag_url: s.flag_url, ministry_tr: s.ministry_tr };
    });
    return m;
  }, [summaries]);

  const loadStatic = async () => {
    try {
      const [s, c] = await Promise.all([fetchSummary(), fetchComparison()]);
      setSummaries(s);
      setComparison(c);
    } catch (e) {
      toast.error("Yükleme hatası", { description: String(e.message || e) });
    }
  };

  const loadStatements = async () => {
    try {
      const st = await fetchStatements(filterCountry || null, dateFrom || null, dateTo || null);
      setStatements(st);
      setLastUpdate(new Date());
    } catch (e) {
      toast.error("Açıklama yükleme hatası", { description: String(e.message || e) });
    }
  };

  useEffect(() => { loadStatic(); }, []);
  useEffect(() => { loadStatements(); /* eslint-disable-next-line */ }, [filterCountry, dateFrom, dateTo]);

  const onCollectAll = async () => {
    setRefreshing(true);
    try {
      await triggerCollect();
      toast.success("Arşiv toplama başladı", {
        description: "12 ülke için tüm dönem verisi toplanıyor (~5-10 dk)",
      });
      setTimeout(() => { loadStatic(); loadStatements(); }, 60_000);
      setTimeout(() => { loadStatic(); loadStatements(); }, 180_000);
      setTimeout(() => { loadStatic(); loadStatements(); }, 360_000);
    } catch (e) {
      toast.error("Başlatma hatası", { description: String(e.message || e) });
    } finally {
      setRefreshing(false);
    }
  };

  const onCollectCountry = async (code) => {
    setRefreshing(true);
    try {
      const r = await collectOneCountry(code);
      await Promise.all([loadStatic(), loadStatements()]);
      toast.success(`${code} güncellendi`, { description: `${r.inserted} kayıt eklendi/güncellendi` });
    } catch (e) {
      toast.error(`${code} yenilenemedi`, { description: e?.response?.data?.detail || String(e.message || e) });
    } finally {
      setRefreshing(false);
    }
  };

  const onDownloadReport = async () => {
    try {
      toast.info("Rapor hazırlanıyor", { description: "Birkaç saniye sürebilir..." });
      await downloadWeeklyReport();
      toast.success("Rapor indirildi");
    } catch (e) {
      toast.error("Rapor indirilemedi", { description: String(e.message || e) });
    }
  };

  const logout = () => { clearToken(); navigate("/", { replace: true }); };

  const tabsConfig = [
    { v: "feed", label: "Akış", icon: Newspaper },
    { v: "cards", label: "Ülke Kartları", icon: LayoutGrid },
    { v: "timeline", label: "Zaman Çizelgesi", icon: LineChartIcon },
    { v: "comparison", label: "Karşılaştırma", icon: TableProperties },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="dashboard-page">
      <div className="tactical-grid">
        <header className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 grid place-items-center border border-red-500/40 bg-red-500/10 rounded-sm">
                <Radar className="h-4 w-4 text-red-500" />
              </div>
              <div>
                <div className="data-label leading-none">RESEARCH · INTERNAL</div>
                <div className="font-heading text-sm sm:text-base leading-tight text-white">
                  İran Savaşı Araştırma Sitesi · 15 Şub - 15 Mart 2026
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <div className="hidden md:block text-[11px] font-mono text-gray-400">
                Son güncelleme: {lastUpdate ? lastUpdate.toLocaleTimeString("tr-TR") : "—"}
              </div>
              <Button size="sm" variant="outline" onClick={onDownloadReport}
                      className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5"
                      data-testid="download-report-btn">
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF Rapor
              </Button>
              <Button size="sm" variant="outline" onClick={onCollectAll} disabled={refreshing}
                      className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5"
                      data-testid="refresh-all-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                Arşiv Topla
              </Button>
              <Button size="sm" variant="ghost" onClick={logout}
                      className="text-gray-300 hover:text-white hover:bg-white/5"
                      data-testid="logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-1.5" /> Çıkış
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="rounded-sm bg-[#141414] border-white/10 p-4">
              <div className="data-label">Ülke Girişi</div>
              <div className="text-3xl font-mono font-bold mt-1">{summaries.length}</div>
            </Card>
            <Card className="rounded-sm bg-[#141414] border-white/10 p-4">
              <div className="data-label">Toplam Açıklama</div>
              <div className="text-3xl font-mono font-bold mt-1">{statements.length}</div>
            </Card>
            <Card className="rounded-sm bg-[#141414] border-white/10 p-4">
              <div className="data-label">Araştırma Başlangıcı</div>
              <div className="text-base font-mono font-bold mt-1">15 Şub 2026</div>
            </Card>
            <Card className="rounded-sm bg-[#141414] border-white/10 p-4">
              <div className="data-label">Araştırma Bitişi</div>
              <div className="text-base font-mono font-bold mt-1">15 Mar 2026</div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="rounded-sm bg-[#141414] border-white/10 p-4 mb-4">
            <div className="flex items-center gap-4 flex-wrap">
              <Search className="h-4 w-4 text-gray-400" />
              <div className="flex-1 min-w-[180px]">
                <div className="data-label mb-1">Ülke Filtresi</div>
                <Select value={filterCountry || "all"} onValueChange={(v) => setFilterCountry(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-country" className="rounded-sm bg-[#0A0A0A] border-white/15 text-white font-mono">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 text-white max-h-[300px]">
                    <SelectItem value="all">Tüm Ülkeler</SelectItem>
                    {summaries.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.name_tr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="data-label mb-1">Başlangıç</div>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                       className="rounded-sm bg-[#0A0A0A] border-white/15 text-white font-mono w-40"
                       data-testid="filter-date-from" />
              </div>
              <div>
                <div className="data-label mb-1">Bitiş</div>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                       className="rounded-sm bg-[#0A0A0A] border-white/15 text-white font-mono w-40"
                       data-testid="filter-date-to" />
              </div>
              <Button size="sm" variant="outline" onClick={() => { setFilterCountry(""); setDateFrom(RESEARCH_START); setDateTo(RESEARCH_END); }}
                      className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 mt-4"
                      data-testid="reset-filters-btn">
                Sıfırla
              </Button>
            </div>
          </Card>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-[#141414] border border-white/10 rounded-sm p-1 h-auto flex-wrap"
                      data-testid="dashboard-tabs">
              {tabsConfig.map((t) => (
                <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                             className="rounded-sm font-mono text-xs uppercase tracking-[0.14em] data-[state=active]:bg-white/10 data-[state=active]:text-white text-gray-400 flex items-center gap-1.5">
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="feed" className="mt-4">
              <StatementFeed statements={statements} countriesByCode={countriesByCode}
                             onRefreshCountry={onCollectCountry} refreshing={refreshing} />
            </TabsContent>
            <TabsContent value="cards" className="mt-4">
              <CountryCards summaries={summaries} onRefreshCountry={onCollectCountry} refreshing={refreshing} />
            </TabsContent>
            <TabsContent value="timeline" className="mt-4">
              <Timeline countriesByCode={countriesByCode} />
            </TabsContent>
            <TabsContent value="comparison" className="mt-4">
              <ComparisonTable data={comparison} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
